import React, {Component, ReactElement, ReactNode} from 'react'
import NatRoomToResidentRepo from '../roomToResident/repository/NatRoomToResidentRepo'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {Nullable} from '../../../app/TypeHelpers'

import {
    ApplyHistoryInfo,
    NatGetRegistrationApplyResponse,
    ResidentUserRegistrationApplyInfo,
} from '../roomToResident/repository/NatGetRegistrationApply'
import {Box, Table, TableBody, TableCell, TableHead, TableRow} from '@mui/material'
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea,
} from '../../../components/shared/hoc/withAdminPageTemplate'
import {
    DO_OR_NOT,
    MESSAGE_TYPE,
    RESIDENT_STATUS,
    RESIDENT_USER_REGISTRATION_APPLY_CATEGORY,
    RESIDENT_USER_REGISTRATION_APPLY_STATUS,
    ROLE_FOR_RESIDENT,
} from '../../../CodeMaster'
import {BackLink, DeleteLink} from '../../../styledComponents/adminPage/Links'
import {AdminPrimaryButton} from '../../../styledComponents/adminPage/Buttons'
import {getStringOrEmptyString, judgeAuthorityCheck} from '../../common/util/Natutil'
import {StyledTableCaption, TableRowName} from '../../../styledComponents/adminPage/Table'
import {formatDateTimeForUpdateDate, formatDateZeroPadding} from '../../../formatters/formatDate'
import {SortableTableHeaderCell} from '../../../styledComponents/adminPage/SortablePaginationTableHeader'
import styled from 'styled-components'
import {HKConstants} from '../../../HKConstants'
import {Messages} from '../../../Messages'
import {
    NatPostRegistrationApplyApproveRequest,
    NatPostRegistrationApplyApproveResponse,
} from '../roomToResident/repository/NatPostRegistrationApplyApprove'
import {HeadingAreaMessageParams} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import {NatAdminCommonDialogComponent} from '../common/admin/NatAdminCommonDialogComponent'
import NatRegistrationApplyRemandForm from './admin/NatRegistrationApplyRemandForm'
import {
    NatPutRegistrationApplyRemandRequest,
    NatPutRegistrationApplyRemandResponse,
} from '../roomToResident/repository/NatPutRegistrationApplyRemand'
import {
    NatDeleteRegistrationApplyRequest,
    NatDeleteRegistrationApplyResponse,
} from '../roomToResident/repository/NatDeleteRegistrationApply'
import NatRegistrationApplyCardKeyNumberForm from './admin/NatRegistrationApplyCardKeyNumberForm'
import {Location} from 'react-router-dom'

interface Props {
    applyId: number
    roomToResidentRepo: NatRoomToResidentRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    residentUserRegistrationApplyInfo: ResidentUserRegistrationApplyInfo
    remandReason: string
    cardKeyNumber: string
    remandDialogOpen: boolean
    cardKeyNumberDialogOpen: boolean
    isLoadCompleted: boolean
}

class NatAdminResidentUserApply extends Component<Props & AdminPagePropsSet, States> {
    applyName: string = '居住者ユーザ申請'

    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        this.state = {
            residentUserRegistrationApplyInfo: {
                applyId: 0,
                applyCategory: '',
                applyStatus: '',
                roomNumber: '',
                condominiumCd: '',
                ridgeCd: '',
                ridgeName: '',
                abolitionSeq: null,
                abolitionRoleId: '',
                abolitionFamilyName: '',
                abolitionFirstName: '',
                abolitionFamilyNameKana: '',
                abolitionFirstNameKana: '',
                abolitionResidentStatus: '',
                abolitionAdminFamilyName: '',
                abolitionAdminFirstName: '',
                abolitionAdminFamilyNameKana: '',
                abolitionAdminFirstNameKana: '',
                abolitionApplyFormFamilyName: '',
                abolitionApplyFormFirstName: '',
                abolitionExpectedDate: '',
                registrationRoleId: '',
                registrationFamilyName: '',
                registrationFirstName: '',
                registrationFamilyNameKana: '',
                registrationFirstNameKana: '',
                registrationStartDate: '',
                cardKeyNumberRequired: DO_OR_NOT.NOT.value,
                version: 0,
                applyHistoryInfo: [],
            },
            remandReason: '',
            cardKeyNumber: '',
            remandDialogOpen: false,
            cardKeyNumberDialogOpen: false,
            isLoadCompleted: false,
        }
        this.handleRemandReasonChange = this.handleRemandReasonChange.bind(this)
        this.handleCardKeyNumberChange = this.handleCardKeyNumberChange.bind(this)
        this.handleCancelClick = this.handleCancelClick.bind(this)
        this.handleCardKeyNumberCancelClick = this.handleCardKeyNumberCancelClick.bind(this)
        this.handleApprovalButtonClick = this.handleApprovalButtonClick.bind(this)
        this.handleApprovalOkClick = this.handleApprovalOkClick.bind(this)
        this.handleRemandCancelClick = this.handleRemandCancelClick.bind(this)
        this.handleRemandButtonClick = this.handleRemandButtonClick.bind(this)
        this.handleRemandOkClick = this.handleRemandOkClick.bind(this)
        this.handleDeleteLinkClick = this.handleDeleteLinkClick.bind(this)
        this.handleDeleteOkClick = this.handleDeleteOkClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            await this.getResidentUserRegistrationApply()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
        this.showInitialMessage()
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

    async getResidentUserRegistrationApply(): Promise<void> {
        const {roomToResidentRepo, applyId} = this.props

        const residentUserApply: Nullable<NatGetRegistrationApplyResponse> = await this.props.asyncCallFunction(
            roomToResidentRepo.getResidentUserApply.bind(
                roomToResidentRepo,
                applyId,
            ),
            (httpErrorOrNetworkError) => {
                this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        if (residentUserApply && residentUserApply.details) {
            this.setState({residentUserRegistrationApplyInfo: residentUserApply.details})
        }
    }

    getDisplayFullName(familyName: Nullable<string>,
                       firstName: Nullable<string>,
                       familyNameKana: Nullable<string> = '',
                       firstNameKana: Nullable<string> = ''): string {
        const fullName = getStringOrEmptyString(familyName) + ' ' + getStringOrEmptyString(firstName)
        const fullNameKana = getStringOrEmptyString(familyNameKana) + ' ' + getStringOrEmptyString(firstNameKana)
        return fullName + (fullNameKana !== ' ' ? '(' + fullNameKana + ')' : '')
    }


    beforeUserAction(): void {
        this.props.eventEmitter.clearHeadingAreaMessage()
    }

    handleCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleApprovalOkClick(): Promise<void> {
        const {roomToResidentRepo, applyId} = this.props
        const {residentUserRegistrationApplyInfo, cardKeyNumber} = this.state

        this.props.eventEmitter.closeDialog()
        await this.props.eventEmitter.showLoading()
        try {
            const request: NatPostRegistrationApplyApproveRequest = {
                applyId: applyId,
                cardKeyNumber: cardKeyNumber,
                version: residentUserRegistrationApplyInfo.version,
            }
            const response: Nullable<NatPostRegistrationApplyApproveResponse> = await this.props.asyncCallFunction(
                roomToResidentRepo.putResidentUserApplyApprove.bind(
                    roomToResidentRepo, request),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )
            if (response && response.requestError === null) {
                this.setState({cardKeyNumber: '', cardKeyNumberDialogOpen: false})
                await this.getResidentUserRegistrationApply()
                await this.props.eventEmitter.showHeadingAreaMessage({
                    title: null,
                    messageLevel: MESSAGE_TYPE.INFORMATION,
                    detail: Messages.IRESA0023.toString(this.applyName),
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async handleApprovalButtonClick(): Promise<void> {
        const {residentUserRegistrationApplyInfo} = this.state

        this.beforeUserAction()
        if (residentUserRegistrationApplyInfo.cardKeyNumberRequired === DO_OR_NOT.DO.value) {
            this.setState({cardKeyNumberDialogOpen: true})
        } else {
            await this.props.eventEmitter.showDialog({
                    title: '確認',
                    errorLevel: Messages.IRESA0022.errorLevel,
                    message: Messages.IRESA0022.toString('承認'),
                    buttonProps: [
                        {
                            isDefault: false,
                            buttonLabel: 'キャンセル',
                            buttonHandler: this.handleCancelClick,
                        },
                        {
                            isDefault: false,
                            buttonLabel: 'OK',
                            buttonHandler: this.handleApprovalOkClick,
                        },
                    ],
                },
            )
        }
    }

    handleCardKeyNumberChange(value: string): void {
        this.setState({cardKeyNumber: value})
    }

    handleCardKeyNumberCancelClick(): void {
        this.setState({cardKeyNumber: '', cardKeyNumberDialogOpen: false})
    }

    handleRemandReasonChange(value: string): void {
        this.setState({remandReason: value})
    }

    handleRemandButtonClick(): void {
        this.beforeUserAction()
        this.setState({remandDialogOpen: true})
    }

    handleRemandCancelClick(): void {
        this.setState({remandReason: '', remandDialogOpen: false})
    }

    async handleRemandOkClick(): Promise<void> {
        const {roomToResidentRepo, applyId} = this.props
        const {residentUserRegistrationApplyInfo, remandReason} = this.state

        await this.props.eventEmitter.showLoading()
        try {
            const request: NatPutRegistrationApplyRemandRequest = {
                applyId: applyId,
                memo: remandReason,
                version: residentUserRegistrationApplyInfo.version,
            }
            const response: Nullable<NatPutRegistrationApplyRemandResponse> = await this.props.asyncCallFunction(
                roomToResidentRepo.putResidentUserApplyRemand.bind(
                    roomToResidentRepo, request),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )
            if (response && response.requestError === null) {
                this.setState({
                    remandReason: '',
                    remandDialogOpen: false,
                }, async () => {
                    await this.getResidentUserRegistrationApply()
                    await this.props.eventEmitter.showHeadingAreaMessage({
                        title: null,
                        messageLevel: MESSAGE_TYPE.INFORMATION,
                        detail: Messages.IRESA0025.toString(this.applyName),
                    })
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async handleDeleteOkClick(): Promise<void> {
        const {roomToResidentRepo, applyId} = this.props

        this.props.eventEmitter.closeDialog()
        await this.props.eventEmitter.showLoading()
        try {
            const request: NatDeleteRegistrationApplyRequest = {
                applyId: applyId,
            }
            const response: Nullable<NatDeleteRegistrationApplyResponse> = await this.props.asyncCallFunction(
                roomToResidentRepo.deleteResidentUserApply.bind(
                    roomToResidentRepo, request),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )
            if (response && response.requestError === null) {
                await this.getResidentUserRegistrationApply()
                await this.props.eventEmitter.showHeadingAreaMessage({
                    title: null,
                    messageLevel: MESSAGE_TYPE.INFORMATION,
                    detail: Messages.IOPCC0009.toString(this.applyName),
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async handleDeleteLinkClick(): Promise<void> {
        this.beforeUserAction()
        await this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IOPCC0011.errorLevel,
                message: Messages.IOPCC0011.toString(this.applyName),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleCancelClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleDeleteOkClick,
                    },
                ],
            },
        )
    }

    renderEditButton(): ReactNode {
        return (
            <AdminPrimaryButton
                onClick={() => this.props.navigate(
                    `/admin/residentUser/applyEdit/${this.props.applyId}`,
                )}>
                編集
            </AdminPrimaryButton>
        )
    }

    renderApprovalButton(): ReactNode {
        return (
            <AdminPrimaryButton
                onClick={this.handleApprovalButtonClick}
            >
                承認
            </AdminPrimaryButton>
        )
    }

    renderRemandButton(): ReactNode {
        return (
            <AdminPrimaryButton
                onClick={this.handleRemandButtonClick}
            >
                差し戻し
            </AdminPrimaryButton>
        )
    }

    renderEditAndApprovalButton(): ReactNode {
        return (
            <AdminPrimaryButton
                onClick={() => this.props.navigate(
                    `/admin/residentUser/applyEdit/${this.props.applyId}`,
                )}>
                編集・承認
            </AdminPrimaryButton>
        )
    }

    renderDeleteLink(): ReactNode {
        return (
            <DeleteLink
                onClick={this.handleDeleteLinkClick}
            >
                居住者ユーザ申請を削除する
            </DeleteLink>
        )
    }

    renderPageHeaderArea(): Nullable<ReactNode> {
        const applyStatus: RESIDENT_USER_REGISTRATION_APPLY_STATUS =
            RESIDENT_USER_REGISTRATION_APPLY_STATUS.codeToEnumConstants(
                this.state.residentUserRegistrationApplyInfo.applyStatus,
            )
        return (
            <>
                <PageFotterAreaRight>
                    <HeaderButtonByApplyStatusAndAuthority
                        applyStatus={applyStatus}
                        displayableApplyStatus={[
                            RESIDENT_USER_REGISTRATION_APPLY_STATUS.APPLYING,
                            RESIDENT_USER_REGISTRATION_APPLY_STATUS.CONFIRMING]}
                        displayableAuthorities={
                            [HKConstants.CMN.AUTH.APPLY_RESIDENT_USER,
                                HKConstants.CMN.AUTH.APPROVAL_RESIDENT_USER]}>
                        {this.renderDeleteLink()}
                    </HeaderButtonByApplyStatusAndAuthority>
                </PageFotterAreaRight>
                <PageFotterAreaLeft>
                    <HeaderButtonByApplyStatusAndAuthority
                        applyStatus={applyStatus}
                        displayableApplyStatus={[RESIDENT_USER_REGISTRATION_APPLY_STATUS.CONFIRMING]}
                        displayableAuthorities={[HKConstants.CMN.AUTH.APPLY_RESIDENT_USER]}>
                        <Box mr={'15px'}>
                            {this.renderEditButton()}
                        </Box>
                    </HeaderButtonByApplyStatusAndAuthority>
                    <HeaderButtonByApplyStatusAndAuthority
                        applyStatus={applyStatus}
                        displayableApplyStatus={[
                            RESIDENT_USER_REGISTRATION_APPLY_STATUS.APPLYING,
                            RESIDENT_USER_REGISTRATION_APPLY_STATUS.CONFIRMING]}
                        displayableAuthorities={[HKConstants.CMN.AUTH.APPROVAL_RESIDENT_USER]}>
                        <Box mr={'15px'}>
                            {this.renderApprovalButton()}
                        </Box>
                    </HeaderButtonByApplyStatusAndAuthority>
                    <HeaderButtonByApplyStatusAndAuthority
                        applyStatus={applyStatus}
                        displayableApplyStatus={[
                            RESIDENT_USER_REGISTRATION_APPLY_STATUS.APPLYING,
                            RESIDENT_USER_REGISTRATION_APPLY_STATUS.CONFIRMING]}
                        displayableAuthorities={[HKConstants.CMN.AUTH.APPROVAL_RESIDENT_USER]}>
                        <Box mr={'15px'}>
                            {this.renderRemandButton()}
                        </Box>
                    </HeaderButtonByApplyStatusAndAuthority>
                    <HeaderButtonByApplyStatusAndAuthority
                        applyStatus={applyStatus}
                        displayableApplyStatus={[
                            RESIDENT_USER_REGISTRATION_APPLY_STATUS.APPLYING,
                            RESIDENT_USER_REGISTRATION_APPLY_STATUS.CONFIRMING]}
                        displayableAuthorities={[HKConstants.CMN.AUTH.APPROVAL_RESIDENT_USER]}>
                        <Box mr={'15px'}>
                            {this.renderEditAndApprovalButton()}
                        </Box>
                    </HeaderButtonByApplyStatusAndAuthority>
                </PageFotterAreaLeft>
            </>
        )
    }

    renderApplyInfo(): ReactNode {
        const {residentUserRegistrationApplyInfo} = this.state

        const applyCategory: RESIDENT_USER_REGISTRATION_APPLY_CATEGORY =
            RESIDENT_USER_REGISTRATION_APPLY_CATEGORY.codeToEnumConstants(
                residentUserRegistrationApplyInfo.applyCategory,
            )

        const applyStatus: RESIDENT_USER_REGISTRATION_APPLY_STATUS =
            RESIDENT_USER_REGISTRATION_APPLY_STATUS.codeToEnumConstants(
                residentUserRegistrationApplyInfo.applyStatus,
            )

        const abolitionRoleId: Nullable<ROLE_FOR_RESIDENT> =
            residentUserRegistrationApplyInfo.abolitionRoleId ?
                ROLE_FOR_RESIDENT.codeToEnumConstants(residentUserRegistrationApplyInfo.abolitionRoleId) : null

        const registrationRoleId: Nullable<ROLE_FOR_RESIDENT> =
            residentUserRegistrationApplyInfo.registrationRoleId ?
                ROLE_FOR_RESIDENT.codeToEnumConstants(residentUserRegistrationApplyInfo.registrationRoleId) : null

        const abolitionResidentStatus: Nullable<RESIDENT_STATUS> =
            residentUserRegistrationApplyInfo.abolitionResidentStatus ?
                RESIDENT_STATUS.codeToEnumConstants(residentUserRegistrationApplyInfo.abolitionResidentStatus) : null

        const abolitionApplyFormFullName: string = this.getDisplayFullName(
            residentUserRegistrationApplyInfo.abolitionApplyFormFamilyName,
            residentUserRegistrationApplyInfo.abolitionApplyFormFirstName,
        )

        const abolitionExpectedDate: string =
            residentUserRegistrationApplyInfo.abolitionExpectedDate ?
                formatDateZeroPadding(residentUserRegistrationApplyInfo.abolitionExpectedDate) : ''

        const registrationStartDate: string =
            residentUserRegistrationApplyInfo.registrationStartDate ?
                formatDateZeroPadding(residentUserRegistrationApplyInfo.registrationStartDate) : ''

        const abolitionFullName: string = this.getDisplayFullName(
            residentUserRegistrationApplyInfo.abolitionFamilyName,
            residentUserRegistrationApplyInfo.abolitionFirstName,
            residentUserRegistrationApplyInfo.abolitionFamilyNameKana,
            residentUserRegistrationApplyInfo.abolitionFirstNameKana,
        )

        const abolitionAdminFullName: string = this.getDisplayFullName(
            residentUserRegistrationApplyInfo.abolitionAdminFamilyName,
            residentUserRegistrationApplyInfo.abolitionAdminFirstName,
            residentUserRegistrationApplyInfo.abolitionAdminFamilyNameKana,
            residentUserRegistrationApplyInfo.abolitionAdminFirstNameKana,
        )

        const registrationFullName: string = this.getDisplayFullName(
            residentUserRegistrationApplyInfo.registrationFamilyName,
            residentUserRegistrationApplyInfo.registrationFirstName,
            residentUserRegistrationApplyInfo.registrationFamilyNameKana,
            residentUserRegistrationApplyInfo.registrationFirstNameKana,
        )

        return (
            <>
                <Table>
                    <StyledTableCaption>申請情報</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableRowName>申請区分</TableRowName>
                            <TableCell colSpan={4}>
                                {applyCategory.label}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName>ステータス</TableRowName>
                            <TableCell colSpan={4}>
                                {applyStatus.label}
                            </TableCell>
                        </TableRow>
                        <TableRowByApplyCategory
                            applyCategory={applyCategory}
                            displayableCategory={[
                                RESIDENT_USER_REGISTRATION_APPLY_CATEGORY.ABOLITION,
                                RESIDENT_USER_REGISTRATION_APPLY_CATEGORY.ABOLITION_REGISTRATION,
                            ]}>
                            <TableRowName>権限</TableRowName>
                            <TableCell colSpan={4}>
                                {abolitionRoleId && abolitionRoleId.label}
                            </TableCell>
                        </TableRowByApplyCategory>
                        <TableRow>
                            <TableRowName>部屋番号</TableRowName>
                            <TableCell colSpan={4}>
                                {residentUserRegistrationApplyInfo.ridgeName +
                                    ' ' +
                                    residentUserRegistrationApplyInfo.roomNumber}
                            </TableCell>
                        </TableRow>
                        <TableRowByApplyCategory
                            applyCategory={applyCategory}
                            displayableCategory={[
                                RESIDENT_USER_REGISTRATION_APPLY_CATEGORY.ABOLITION,
                                RESIDENT_USER_REGISTRATION_APPLY_CATEGORY.ABOLITION_REGISTRATION,
                            ]}>
                            <TableRowName>廃止居住者ユーザ 氏名</TableRowName>
                            <TableCell colSpan={2}>
                                {abolitionFullName}
                            </TableCell>
                            <TableRowName>廃止居住者ユーザ ステータス</TableRowName>
                            <TableCell colSpan={2}>
                                {abolitionResidentStatus && abolitionResidentStatus.label}
                            </TableCell>
                        </TableRowByApplyCategory>
                        <TableRowByApplyCategory
                            applyCategory={applyCategory}
                            displayableCategory={[
                                RESIDENT_USER_REGISTRATION_APPLY_CATEGORY.ABOLITION,
                                RESIDENT_USER_REGISTRATION_APPLY_CATEGORY.ABOLITION_REGISTRATION,
                            ]}>
                            <TableRowName>廃止居住者ユーザ 管理者確認用氏名</TableRowName>
                            <TableCell colSpan={4}>
                                {abolitionAdminFullName}
                            </TableCell>
                        </TableRowByApplyCategory>
                        <TableRowByApplyCategory
                            applyCategory={applyCategory}
                            displayableCategory={[
                                RESIDENT_USER_REGISTRATION_APPLY_CATEGORY.ABOLITION,
                                RESIDENT_USER_REGISTRATION_APPLY_CATEGORY.ABOLITION_REGISTRATION,
                            ]}>
                            <TableRowName>廃止申請書 氏名</TableRowName>
                            <TableCell colSpan={4}>
                                {abolitionApplyFormFullName}
                            </TableCell>
                        </TableRowByApplyCategory>
                        <TableRowByApplyCategory
                            applyCategory={applyCategory}
                            displayableCategory={[
                                RESIDENT_USER_REGISTRATION_APPLY_CATEGORY.ABOLITION,
                                RESIDENT_USER_REGISTRATION_APPLY_CATEGORY.ABOLITION_REGISTRATION,
                            ]}>
                            <TableRowName>廃止予定日</TableRowName>
                            <TableCell colSpan={4}>
                                {abolitionExpectedDate}
                            </TableCell>
                        </TableRowByApplyCategory>
                        <TableRowByApplyCategory
                            applyCategory={applyCategory}
                            displayableCategory={[
                                RESIDENT_USER_REGISTRATION_APPLY_CATEGORY.REGISTRATION,
                                RESIDENT_USER_REGISTRATION_APPLY_CATEGORY.ABOLITION_REGISTRATION,
                            ]}>
                            <TableRowName>新規登録居住者ユーザ 氏名</TableRowName>
                            <TableCell colSpan={4}>
                                {registrationFullName}
                            </TableCell>
                        </TableRowByApplyCategory>
                        <TableRowByApplyCategory
                            applyCategory={applyCategory}
                            displayableCategory={[
                                RESIDENT_USER_REGISTRATION_APPLY_CATEGORY.REGISTRATION,
                                RESIDENT_USER_REGISTRATION_APPLY_CATEGORY.ABOLITION_REGISTRATION,
                            ]}>
                            <TableRowName>新規登録居住者ユーザ 権限</TableRowName>
                            <TableCell colSpan={4}>
                                {registrationRoleId && registrationRoleId.label}
                            </TableCell>
                        </TableRowByApplyCategory>
                        <TableRowByApplyCategory
                            applyCategory={applyCategory}
                            displayableCategory={[
                                RESIDENT_USER_REGISTRATION_APPLY_CATEGORY.REGISTRATION,
                                RESIDENT_USER_REGISTRATION_APPLY_CATEGORY.ABOLITION_REGISTRATION,
                            ]}>
                            <TableRowName>利用開始日</TableRowName>
                            <TableCell colSpan={4}>
                                {registrationStartDate}
                            </TableCell>
                        </TableRowByApplyCategory>
                    </TableBody>
                </Table>
            </>
        )
    }

    renderHistoryInfo(): ReactNode {
        const headers: SortableTableHeaderCell[] = [
            {id: 'registerDatetime', label: '日時'},
            {id: 'applyStatus', label: 'ステータス'},
            {id: 'rep', label: '担当者'},
            {id: 'comment', label: 'コメント'},
        ]

        return (
            <>
                <Table>
                    <StyledTableCaption>履歴</StyledTableCaption>
                </Table>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {headers.map((header) => {
                                return (
                                    <TableCell key={header.id} align={'center'}>{header.label}</TableCell>
                                )
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {this.state.residentUserRegistrationApplyInfo.applyHistoryInfo.map(
                            (applyHistoryInfo, index) => this.renderHistoryRecord(applyHistoryInfo, index),
                        )}
                    </TableBody>
                </Table>
            </>
        )
    }

    renderHistoryRecord(applyHistoryInfo: ApplyHistoryInfo, index: number): ReactElement {
        const registerDatetime: string = applyHistoryInfo.registerDatetime ?
            formatDateTimeForUpdateDate(applyHistoryInfo.registerDatetime) : ''

        const applyStatus: RESIDENT_USER_REGISTRATION_APPLY_STATUS =
            RESIDENT_USER_REGISTRATION_APPLY_STATUS.codeToEnumConstants(
                applyHistoryInfo.applyStatus,
            )

        const repFullName: string = this.getDisplayFullName(
            applyHistoryInfo.userFamilyName,
            applyHistoryInfo.userFirstName,
        )

        return (
            <TableRow key={index}>
                <TableCellWordBreakAll align={'center'}>
                    {registerDatetime}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll align={'center'}>
                    {applyStatus.label}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll align={'center'}>
                    {repFullName}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll align={'center'}>
                    {applyHistoryInfo.memo}
                </TableCellWordBreakAll>
            </TableRow>
        )
    }

    renderPageFooterArea(): ReactNode {
        return (
            <>
                <PageFotterAreaLeft>
                    <BackLink
                        onClick={() => this.props.navigate(
                            `/admin/residentUser/applyList`,
                        )}
                    >
                        居住者ユーザ申請一覧へ戻る
                    </BackLink>
                </PageFotterAreaLeft>
            </>
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted
        ) {
            return null
        }

        const remandForm = (<NatRegistrationApplyRemandForm
            remandReason={this.state.remandReason}
            eventEmitter={this.props.eventEmitter}
            onChange={this.handleRemandReasonChange}
        />)
        const cardKeyForm = (<NatRegistrationApplyCardKeyNumberForm
            cardKeyNumber={this.state.cardKeyNumber}
            eventEmitter={this.props.eventEmitter}
            onChange={this.handleCardKeyNumberChange}
        />)
        return (
            <>
                <NatAdminCommonDialogComponent
                    params={{
                        title: '確認',
                        errorLevel: Messages.IRESA0029.errorLevel,
                        message: Messages.IRESA0029.toString('承認'),
                        buttonProps: [
                            {
                                isDefault: false,
                                buttonLabel: 'キャンセル',
                                buttonHandler: this.handleCardKeyNumberCancelClick,
                                buttonDisabled: false,
                            },
                            {
                                isDefault: false,
                                buttonLabel: 'OK',
                                buttonHandler: this.handleApprovalOkClick,
                                buttonDisabled: !this.state.cardKeyNumber,
                            },
                        ],
                    }}
                    inputAreaComponent={cardKeyForm}
                    open={this.state.cardKeyNumberDialogOpen}/>
                <NatAdminCommonDialogComponent
                    params={{
                        title: '確認',
                        errorLevel: Messages.IRESA0024.errorLevel,
                        message: Messages.IRESA0024.toString(),
                        buttonProps: [
                            {
                                isDefault: false,
                                buttonLabel: 'キャンセル',
                                buttonHandler: this.handleRemandCancelClick,
                                buttonDisabled: false,
                            },
                            {
                                isDefault: false,
                                buttonLabel: 'OK',
                                buttonHandler: this.handleRemandOkClick,
                                buttonDisabled: !this.state.remandReason,
                            },
                        ],
                    }}
                    inputAreaComponent={remandForm}
                    open={this.state.remandDialogOpen}/>
                <Box mb={'15px'}>
                    <PageFooterArea>
                        {this.renderPageHeaderArea()}
                    </PageFooterArea>
                </Box>
                <PageMainArea>
                    {this.renderApplyInfo()}
                </PageMainArea>
                <PageMainArea>
                    {this.renderHistoryInfo()}
                </PageMainArea>
                <PageFooterArea>
                    {this.renderPageFooterArea()}
                </PageFooterArea>
            </>
        )
    }
}

const HeaderButtonByApplyStatusAndAuthority: React.FC<{
    applyStatus: RESIDENT_USER_REGISTRATION_APPLY_STATUS,
    displayableApplyStatus: RESIDENT_USER_REGISTRATION_APPLY_STATUS[],
    displayableAuthorities: string[],
    children: ReactNode
}> = props => {
    let authorityCheck: boolean = false

    let ownAuthority: string = ''
    if (judgeAuthorityCheck(HKConstants.CMN.AUTH.APPLY_RESIDENT_USER)) {
        ownAuthority = HKConstants.CMN.AUTH.APPLY_RESIDENT_USER
    }
    if (judgeAuthorityCheck(HKConstants.CMN.AUTH.APPROVAL_RESIDENT_USER)) {
        // 申請権限と承認権限を保持している場合、承認権限として後続処理を実施
        ownAuthority = HKConstants.CMN.AUTH.APPROVAL_RESIDENT_USER
    }

    for (const displayableAuthority of props.displayableAuthorities) {
        authorityCheck = ownAuthority === displayableAuthority
        if (authorityCheck) {
            break
        }
    }
    if (authorityCheck && props.displayableApplyStatus.includes(props.applyStatus)) {
        return (<>{props.children}</>)
    }
    return (<></>)
}

const TableRowByApplyCategory: React.FC<{
    applyCategory: RESIDENT_USER_REGISTRATION_APPLY_CATEGORY,
    displayableCategory: RESIDENT_USER_REGISTRATION_APPLY_CATEGORY[],
    children: ReactNode
}> = props => {
    if (props.displayableCategory.includes(props.applyCategory)) {
        return (<TableRow>{props.children}</TableRow>)
    }
    return (<></>)
}

export default createAdminPage(NatAdminResidentUserApply)

const TableCellWordBreakAll = styled(TableCell)`
  &.MuiTableCell-root {
    word-break: break-all;
  }
`
